/**
 *
 * Sidebar
 *
 */

import { useDndMonitor, useDroppable } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { ClickAwayListener, Collapse } from '@mui/material';
import { format, parseISO } from 'date-fns';
import Picker, { IEmojiData } from 'emoji-picker-react';
import React, { MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useMobileMediaQuery from '../../../../../hooks/useMobileMediaQuery';
import { useAppDispatch, useAppSelector } from '../../../../../redux/hooks';
import { selectCards, selectEvents } from '../../../../../redux/trip/trip.selectors';
import {
  addListToDivider,
  removeDivider,
  updateActiveListId,
  updateDivider,
} from '../../../../../redux/trip/trip.slice';
import { Trip, TripDivider } from '../../../../../redux/trip/trip.types';
import { getOrderedListsForDivider } from '../../../../../redux/trip/trip.utils';
import { selectCurrentUser } from '../../../../../redux/user/user.selectors';
import { ItemType } from '../../../../../types/DragAndDrop';
import { SORTABLE_TRANSITION } from '../../../../Trip/DnD/types';
import { useSidebarUI } from '../../SidebarUIContext';
import ItemOptionsMenu from '../ItemOptionsMenu/ItemOptionsMenu';
import ListItem from '../ListItem/ListItem';
import RenameListInput from '../RenameListInput/RenameListInput';
import '../AddListInput/AddListInput.scss';
import './DividerItem.scss';

type DividerItemProps = {
  tripId: string;
  divider: TripDivider;
  listOrder: string[];
  trip: Trip;
  activeListId?: string;
  canEdit: boolean;
  isExpanded: boolean;
  onToggle: () => void;
  editingListId: string | null;
  onStartEditingList: (listId: string) => void;
  onFinishEditingList: () => void;
};

const DEFAULT_LIST_EMOJI = '🏝️';
const MAX_CHARACTER_COUNT = 100;
const CANCEL_TIMEOUT = 100;

function DividerItem({
  tripId,
  divider,
  listOrder,
  trip,
  activeListId,
  canEdit,
  isExpanded,
  onToggle,
  editingListId,
  onStartEditingList,
  onFinishEditingList,
}: DividerItemProps) {
  const isMobile = useMobileMediaQuery();
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector(selectCurrentUser);
  const events = useAppSelector(selectEvents);
  const cards = useAppSelector(selectCards);
  const { openEmojiId, setOpenEmojiId, openMenuId, setOpenMenuId } = useSidebarUI();
  const isEditingEmoji = openEmojiId === divider.id;
  const anchorElRef = useRef<HTMLDivElement | null>(null);
  const anchorEl = openMenuId === divider.id ? anchorElRef.current : null;
  const [isEditingName, setIsEditingName] = useState(false);
  const [isAddingList, setIsAddingList] = useState(false);
  const [newListName, setNewListName] = useState('');
  const cancelTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const index = useMemo(() => listOrder.indexOf(divider.id), [listOrder, divider.id]);
  // lists inside this divider, ordered by divider.listOrder
  const nestedLists = useMemo(() => getOrderedListsForDivider(trip, divider), [trip, divider]);

  // Sortable: makes this divider draggable/reorderable at the top level
  const {
    setNodeRef: setSortableRef,
    transform,
    transition,
    isDragging,
    listeners,
    attributes,
  } = useSortable({
    id: divider.id,
    data: { type: ItemType.DIVIDER, index },
    disabled: !canEdit || isEditingName,
    transition: SORTABLE_TRANSITION,
  });

  // Droppable on the container: accepts lists dropped INTO this divider
  const { setNodeRef: setContainerDropRef } = useDroppable({
    id: `divider-container-${divider.id}`,
    data: { type: 'divider-container', dividerId: divider.id },
  });

  // Track when a list is being dragged over this divider for the highlight indicator.
  // useDroppable's isOver is unreliable here because the sortable and droppable
  // share the same DOM node — collision detection picks the sortable id first.
  const [isListDragOver, setIsListDragOver] = useState(false);
  useDndMonitor({
    onDragMove(event) {
      const isList = event.active.data.current?.type === ItemType.LIST;
      // Only highlight when the dragged list is coming from outside this divider
      // (top-level list or a list belonging to a different divider).
      const activeDividerId = event.active.data.current?.dividerId as string | undefined;
      const isFromElsewhere = activeDividerId !== divider.id;
      const overId = event.over?.id as string | undefined;
      const overDividerId = event.over?.data.current?.dividerId as string | undefined;
      // Highlight when hovering the divider's own sortable/droppable nodes, OR
      // when hovering any nested list that belongs to this divider.
      const isOverThis =
        overId === divider.id ||
        overId === `divider-container-${divider.id}` ||
        overDividerId === divider.id ||
        (overId !== undefined && divider.listOrder.includes(overId as string));
      setIsListDragOver(isList && isFromElsewhere && isOverThis);
    },
    onDragEnd() {
      setIsListDragOver(false);
    },
    onDragCancel() {
      setIsListDragOver(false);
    },
  });

  // Droppable on the header: accepts dividers dropped for reorder positioning
  const { setNodeRef: setHeaderDropRef } = useDroppable({
    id: `divider-header-${divider.id}`,
    data: { type: 'divider-header', dividerId: divider.id },
  });

  // Merge the sortable ref + container-droppable ref onto the same DOM node
  const setContainerRef = useCallback(
    (node: HTMLDivElement | null) => {
      setSortableRef(node);
      setContainerDropRef(node);
    },
    [setSortableRef, setContainerDropRef]
  );

  // Builds the list of cards (across all nested lists) that have itinerary events scheduled.
  // Used to warn the user before deletion that removing this divider will also clear those events.
  const scheduledCards = useMemo(() => {
    return nestedLists.flatMap((list) =>
      (list.cardOrder ?? [])
        .map((cardId) => {
          const dayLabels = Object.values(events ?? {})
            .filter((e) => e.cardId === cardId)
            .map((e) => format(parseISO(e.startDateISO), 'EEEE, MMMM d'))
            .sort();
          const cardName = cards?.[cardId]?.customName ?? cards?.[cardId]?.name ?? cardId;
          return dayLabels.length > 0
            ? { cardId, cardName, listName: list.name, listEmoji: list.emoji, dayLabels }
            : null;
        })
        .filter(Boolean)
    ) as Array<{
      cardId: string;
      cardName: string;
      listName: string;
      listEmoji?: string;
      dayLabels: string[];
    }>;
  }, [nestedLists, events, cards]);

  // Rendered inside the delete confirmation dialog when scheduledCards is non-empty.
  // Shows each affected card name, its parent list, and which days it's scheduled on.
  const itineraryWarning =
    scheduledCards.length > 0 ? (
      <span style={{ display: 'block', marginTop: 8 }}>
        The following cards are also scheduled in your itinerary and will be removed:
        <ul style={{ margin: '6px 0 0', paddingLeft: 20 }}>
          {scheduledCards.map(({ cardId, cardName, listName, listEmoji, dayLabels }) => (
            <li key={cardId}>
              <strong>{cardName}</strong> ({listEmoji ? <>{listEmoji} </> : null}
              {listName})
              {dayLabels.map((day) => (
                <div
                  key={day}
                  style={{ paddingLeft: 8 }}
                >
                  - {day}
                </div>
              ))}
            </li>
          ))}
        </ul>
      </span>
    ) : null;

  // expands or collapses the divider; auto-selects the first nested list when expanding
  const handleToggle = useCallback(() => {
    onToggle();
    if (!isExpanded) {
      if (divider.listOrder.length > 0) {
        dispatch(updateActiveListId(divider.listOrder[0]));
      }
    } else {
      dispatch(updateActiveListId(''));
    }
  }, [onToggle, isExpanded, divider.listOrder, dispatch]);

  // opens the ••• options menu for this divider
  const handleOptionsOpen = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      event.stopPropagation();
      anchorElRef.current = event.currentTarget;
      setOpenMenuId(divider.id);
    },
    [divider.id, setOpenMenuId]
  );

  // shared logic for opening the emoji picker; used by both click handlers below
  const openEmojiPicker = useCallback(() => {
    setOpenMenuId(null);
    setTimeout(() => setOpenEmojiId(divider.id), 0);
  }, [divider.id, setOpenEmojiId, setOpenMenuId]);

  // opens the emoji picker when the emoji span is clicked directly (desktop only)
  const handleEmojiSpanClick = useMemo(
    () =>
      canEdit
        ? (e: React.MouseEvent<HTMLSpanElement>) => {
            e.stopPropagation();
            openEmojiPicker();
          }
        : undefined,
    [canEdit, openEmojiPicker]
  );

  // opens the emoji picker from the options menu (passed to DividerOptions)
  const handleDividerEmojiEdit = useMemo(
    () =>
      canEdit
        ? (e: React.MouseEvent<HTMLLIElement>) => {
            e.stopPropagation();
            openEmojiPicker();
          }
        : undefined,
    [canEdit, openEmojiPicker]
  );

  // saves the emoji the user picked and closes the picker
  const onEmojiClick = useCallback(
    (event: MouseEvent, emojiObject: IEmojiData) => {
      event.stopPropagation();
      setOpenEmojiId(null);
      dispatch(
        updateDivider({ tripId, dividerId: divider.id, field: 'emoji', value: emojiObject.emoji })
      );
    },
    [dispatch, tripId, divider.id, setOpenEmojiId]
  );

  // closes the emoji picker when the user clicks outside it
  const handleClickAway = useCallback(() => setOpenEmojiId(null), [setOpenEmojiId]);

  // saves a new name for this divider
  const onSaveName = useCallback(
    (name: string) => {
      dispatch(updateDivider({ tripId, dividerId: divider.id, field: 'name', value: name }));
    },
    [dispatch, tripId, divider.id]
  );

  // deletes this divider and moves all its nested lists back to the top level
  const handleDelete = useCallback(() => {
    const allCardIds = nestedLists.flatMap((l) => l.cardOrder ?? []);
    dispatch(
      removeDivider({
        tripId,
        dividerId: divider.id,
        tripListOrder: listOrder,
        nestedListIds: divider.listOrder,
        allCardIds,
      })
    );
  }, [dispatch, tripId, divider.id, listOrder, divider.listOrder, nestedLists]);

  // shows the inline input for adding a new list inside this divider
  const showAddList = useCallback(() => setIsAddingList(true), []);

  // saves the new list and adds it to this divider
  const onAddList = useCallback(() => {
    setIsAddingList(false);
    if (tripId && currentUser && newListName.trim().length > 0) {
      dispatch(
        addListToDivider({
          tripId,
          dividerId: divider.id,
          list: {
            name: newListName.trim(),
            emoji: DEFAULT_LIST_EMOJI,
            creator: currentUser.uid,
          },
          dividerListOrder: divider.listOrder,
        })
      );
      setNewListName('');
    }
  }, [tripId, currentUser, newListName, divider.id, divider.listOrder, dispatch]);

  // cancels the add-list input with a short delay so a save click can register before the blur fires
  const cancelAddList = useCallback(() => {
    if (cancelTimeoutRef.current) {
      clearTimeout(cancelTimeoutRef.current);
    }
    cancelTimeoutRef.current = setTimeout(() => {
      setIsAddingList(false);
      setNewListName('');
      cancelTimeoutRef.current = null;
    }, CANCEL_TIMEOUT);
  }, []);

  // clears any pending cancel timeout on unmount
  useEffect(() => {
    return () => {
      if (cancelTimeoutRef.current) {
        clearTimeout(cancelTimeoutRef.current);
      }
    };
  }, []);

  // handles Enter (save), Escape (cancel), and enforces the character limit while typing
  const handleAddListKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      const target = event.target as HTMLInputElement;
      if (event.key === 'Enter' && target.value.trim().length > 0) {
        onAddList();
        return;
      }
      if (event.key === 'Escape') {
        cancelAddList();
        return;
      }
      if (
        target.value.length >= MAX_CHARACTER_COUNT &&
        event.key !== 'Backspace' &&
        event.key !== 'ArrowLeft' &&
        event.key !== 'ArrowRight'
      ) {
        event.preventDefault();
      }
    },
    [onAddList, cancelAddList]
  );

  // builds the CSS class string based on current drag/drop state
  const containerClass = useMemo(
    () =>
      ['divider-item-container', isDragging && 'dragging', isListDragOver && 'is-list-over']
        .filter(Boolean)
        .join(' '),
    [isDragging, isListDragOver]
  );

  return (
    <>
      <div
        ref={setContainerRef}
        className={containerClass}
        style={{ transform: CSS.Translate.toString(transform), transition }}
      >
        <div
          ref={setHeaderDropRef}
          className='divider-header'
          onClick={handleToggle}
          {...listeners}
          {...attributes}
        >
          <ChevronRightRoundedIcon className={`divider-chevron${isExpanded ? ' expanded' : ''}`} />

          {isEditingName ? (
            <div onClick={(e) => e.stopPropagation()}>
              <RenameListInput
                tripId={tripId}
                list={divider}
                canEdit={canEdit}
                isEditingName={isEditingName}
                setIsEditingName={setIsEditingName}
                isMobile={isMobile}
                onSave={onSaveName}
              />
            </div>
          ) : (
            <div className='divider-info'>
              <span
                className='divider-emoji'
                onClick={isMobile ? undefined : handleEmojiSpanClick}
              >
                {divider.emoji}
              </span>
              <span
                className='divider-name'
                title={divider.name}
              >
                {divider.name}
              </span>
            </div>
          )}

          {canEdit && !isEditingName && (
            <div
              className='divider-header-actions'
              onClick={(e) => e.stopPropagation()}
            >
              {!isAddingList && (
                <div
                  className='divider-add-btn'
                  onClick={showAddList}
                >
                  +
                </div>
              )}
              <div
                className={`list-options-div${isMobile ? ' mobile' : ''}`}
                onClick={handleOptionsOpen}
              >
                •••
              </div>
            </div>
          )}
        </div>

        {isEditingEmoji && (
          <ClickAwayListener onClickAway={handleClickAway}>
            <div
              className={`emoji-picker-div${isMobile ? ' mobile' : ''}`}
              onClick={(e) => e.stopPropagation()}
            >
              <Picker onEmojiClick={onEmojiClick} />
            </div>
          </ClickAwayListener>
        )}

        {isAddingList && (
          <div className='divider-add-list-input'>
            <input
              className='styled-input'
              placeholder='e.g. Restaurants'
              value={newListName}
              onChange={(e) => setNewListName(e.target.value)}
              onBlur={cancelAddList}
              onKeyDown={handleAddListKeyDown}
              autoFocus
            />
            <div className='new-list-buttons'>
              <div
                className='cancel-add-item-div'
                onClick={cancelAddList}
              >
                <CloseRoundedIcon className='cancel-add-item-button' />
                <span className='cancel-span'>CANCEL</span>
              </div>
              {newListName.trim().length > 0 && (
                <div
                  className='add-item-div'
                  onClick={onAddList}
                >
                  <CheckRoundedIcon className='save-add-item-button' />
                  <span className='add-span'>SAVE</span>
                </div>
              )}
            </div>
          </div>
        )}

        <Collapse
          in={isExpanded}
          timeout={200}
          unmountOnExit
        >
          <div className='divider-lists'>
            <SortableContext
              items={divider.listOrder}
              strategy={verticalListSortingStrategy}
            >
              {nestedLists.map((list) => (
                <ListItem
                  key={list.id}
                  tripId={tripId}
                  list={list}
                  listOrder={divider.listOrder}
                  dividerId={divider.id}
                  isActive={list.id === activeListId}
                  canEdit={canEdit}
                  isEditingName={list.id === editingListId}
                  onStartEditing={() => onStartEditingList(list.id)}
                  onFinishEditing={onFinishEditingList}
                  cardCount={list.cardOrder?.length ?? 0}
                />
              ))}
            </SortableContext>
          </div>
        </Collapse>
      </div>

      <ItemOptionsMenu
        itemName={divider.name}
        isMobile={isMobile}
        anchorEl={anchorEl}
        setAnchorEl={() => setOpenMenuId(null)}
        onRename={() => setIsEditingName(true)}
        onEmojiEdit={handleDividerEmojiEdit}
        onDelete={handleDelete}
        deleteMessage={
          <>
            Deleting {divider.emoji ? <>{divider.emoji} </> : null}
            <strong>{divider.name}</strong> will remove all lists and places inside it.
            {itineraryWarning}
          </>
        }
      />
    </>
  );
}

DividerItem.displayName = 'DividerItem';
export default DividerItem;
